import { DeleteOutline } from '@mui/icons-material'
import { Box, BoxProps, Grid, styled, Typography, useTheme } from '@mui/material'
import React from 'react'
import { UseFormReturn } from 'react-hook-form'
import { MUIThemeColors } from 'src/mui-theme'
import { GetRGBA, getThemeColor } from 'src/utils'
import { FormField, FormLayout, getFieldKey } from '../../generated-types/form-field'
import { AddIcon } from '../../screens/customer/form/Icons'
import FieldSelector from './FieldSelector'

interface ILayoutProps {
  layout: FormLayout
  layoutIndex: number
  size: number
  onDrop: (index: number, fieldIndex: number) => void
  onRearrange?: (layoutIndex: number, colIndex: number, fieldIndex: number) => void
  onCreateLayout: (index: number) => void
  deleteLayout: () => void
  setSelectedSettingField: (field: FormField, row: number, col: number, fieldIndex: number) => void
  disabled: boolean
  indexCol: number
  form?: UseFormReturn
  withColorLayout?: boolean
  onContentChange?: (field: FormField, row: number, col: number, fieldIndex: number) => void
  /** Tighter layout for schema fill dialogs */
  density?: 'default' | 'compact'
  previewMode?: boolean
  isDraft?: boolean
}

const LayoutBox: React.FC<ILayoutProps> = ({
  layout,
  onDrop,
  form,
  layoutIndex,
  size,
  onRearrange,
  onCreateLayout,
  deleteLayout,
  setSelectedSettingField,
  disabled,
  indexCol,
  withColorLayout = false,
  onContentChange,
  density = 'default',
  previewMode = false,
  isDraft = false,
}) => {
  const theme = useTheme()
  const { fields } = layout
  const isCompact = density === 'compact'
  return (
    <Grid
      item
      md={size}
      sx={{
        pt: isCompact && !withColorLayout ? 0 : '5px',
        ...(withColorLayout
          ? {
              bgcolor: GetRGBA(getThemeColor(theme, MUIThemeColors.paleBlue), 0.14),
              minHeight: 50,
            }
          : {}),
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        height="100%"
        borderRadius={1}
        sx={
          withColorLayout
            ? {
                border: `1px solid ${getThemeColor(theme, MUIThemeColors.paleBlue)}`,
              }
            : undefined
        }
      >
        <OuterContainer
          sx={{
            ...(disabled && !previewMode
              ? {
                  width: 'calc(100% - 30px)',
                }
              : {
                  width: '100%',
                }),
            ...(isCompact ? { gap: theme.spacing(0.75) } : {}),
          }}
        >
          {fields.map((field: FormField, index: number) => {
            return (
              <DroppableBox
                key={index}
                isReArrangeable={Boolean(onRearrange)}
                onDragEnter={
                  onRearrange
                    ? event => {
                        event.currentTarget.classList.add('drag-enter')
                      }
                    : undefined
                }
                onDragLeave={
                  onRearrange
                    ? event => {
                        event.currentTarget.classList.remove('drag-enter')
                      }
                    : undefined
                }
                onDragOver={
                  onRearrange
                    ? event => {
                        event.preventDefault()
                      }
                    : undefined
                }
                onDrop={
                  onRearrange
                    ? event => {
                        event.currentTarget.classList.remove('drag-enter')
                        onDrop(layoutIndex, index)
                      }
                    : undefined
                }
              >
                <FieldSelector
                  form={form}
                  fieldDisabled={field?.disabled}
                  disabled={disabled}
                  density={density}
                  onChange={value => {
                    if (form) {
                      form.setValue(getFieldKey(field), value)
                    }
                  }}
                  layoutIndex={layoutIndex}
                  onDrag={() => {
                    if (onRearrange) onRearrange(layoutIndex, indexCol, index)
                  }}
                  index={index}
                  field={field}
                  setSelectedSettingField={() => {
                    if (setSelectedSettingField) {
                      setSelectedSettingField(field, layoutIndex, indexCol, index)
                    }
                  }}
                  onContentChange={
                    onContentChange
                      ? updatedField => onContentChange(updatedField, layoutIndex, indexCol, index)
                      : undefined
                  }
                  isDraft={isDraft}
                />
                {form?.formState.errors[getFieldKey(field)]?.message != null && (
                  <Box sx={{ mt: 0.5, ml: 0.5 }}>
                    <Typography variant="caption" color="error" component="p" sx={{ m: 0 }}>
                      {String(form.formState.errors[getFieldKey(field)]?.message)}
                    </Typography>
                  </Box>
                )}
              </DroppableBox>
            )
          })}
          {onRearrange && (
            <DroppableBox
              isReArrangeable
              onDragEnter={event => {
                event.currentTarget.classList.add('drag-enter')
              }}
              onDragLeave={event => {
                event.currentTarget.classList.remove('drag-enter')
              }}
              onDrop={event => {
                event.currentTarget.classList.remove('drag-enter')
                onDrop(layoutIndex, fields.length)
              }}
            >
              <StyledContainer
                onDragOver={event => {
                  event.preventDefault()
                }}
                onDragEnter={event => {
                  event.preventDefault()
                }}
              ></StyledContainer>
            </DroppableBox>
          )}
        </OuterContainer>
        {disabled && !previewMode && (
          <Box width={30} height={'100%'}>
            <AddIcon
              onClick={() => {
                onCreateLayout(indexCol + 1)
              }}
            />
            <StyledDeleteButton onClick={() => deleteLayout()} />
          </Box>
        )}
      </Box>
    </Grid>
  )
}

export const StyledContainer = styled(Box)({
  height: '100%',
  minHeight: 50,
})

export const StyledDeleteButton = styled(DeleteOutline)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.paleBlue),
  cursor: 'pointer',
}))

export const OuterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
}))

interface DroppableBoxProps extends BoxProps {
  isReArrangeable: boolean
}

const DroppableBox = styled(Box)<DroppableBoxProps>(({ isReArrangeable }) =>
  isReArrangeable
    ? {
        width: 'calc(100% - 30px)',
        minHeight: 50,
      }
    : undefined,
)

export default LayoutBox
